import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryFetch } from 'sonata-react-components';

import { AudienceInsights } from '@/components/common/AudienceInsights/AudienceInsights';
import { useBuyerInsightsSocket } from '@/hooks/useBuyerInsightsSocket';
import { getBuyerInsights } from '@/services/footfall.service';
import { BuyerInsightsLoader } from './BuyerInsightsLoader/BuyerInsightsLoader';

import type { AudienceInsightsLabels } from '@/components/common/AudienceInsights/AudienceInsights';
import type { BuyerInsightsApiResponse, BuyerInsightsData } from '@/types/footfall';

type Props = {
  entityType: string;
  entityId: string;
  country?: string;
  activePoiId: string;
};

export function BuyerInsights({ entityType, entityId, country: _country, activePoiId }: Props) {
  const { formatMessage } = useIntl();
  const [wsData, setWsData] = useState<BuyerInsightsData | null>(null);

  const { data: queryData, isLoading } = useQueryFetch<BuyerInsightsApiResponse>(
    ['footfall', 'buyerInsights', entityType, entityId, activePoiId],
    () => getBuyerInsights(entityType, entityId, activePoiId)
  );

  const status = wsData !== null ? 'processed' : (queryData?.status ?? 'idle');
  const data = wsData ?? queryData?.data ?? null;
  const week = status === 'processing' ? (queryData?.week ?? null) : null;

  useBuyerInsightsSocket({ activePoiId, entityId, onReady: setWsData, week });

  if (isLoading || status === 'processing') {
    return <BuyerInsightsLoader />;
  }

  if (status === 'idle' || status === 'error' || !data) {
    return null;
  }

  const labels: AudienceInsightsLabels = {
    affinitiesHeader: formatMessage({
      defaultMessage: "Buyer's Lifestyle & Affinities",
      id: 'driveToStore.footfallReport.buyerInsights.affinities.header'
    }),
    avgAge: formatMessage({
      defaultMessage: 'AVG AGE',
      id: 'driveToStore.footfallReport.buyerInsights.avgAge'
    }),
    bottomBrands: formatMessage({
      defaultMessage: 'Low brands affinities',
      id: 'driveToStore.footfallReport.buyerInsights.bottomBrands'
    }),
    coreNeeds: formatMessage({
      defaultMessage: 'CORE NEEDS',
      id: 'driveToStore.footfallReport.buyerInsights.coreNeeds'
    }),
    genderLabel: formatMessage({
      defaultMessage: 'GENDER',
      id: 'driveToStore.footfallReport.buyerInsights.gender.label'
    }),
    header: formatMessage({
      defaultMessage: 'Buyer Insights',
      id: 'driveToStore.footfallReport.buyerInsights.header'
    }),
    highAffinity: formatMessage({
      defaultMessage: 'High',
      id: 'driveToStore.footfallReport.buyerInsights.affinity.high'
    }),
    interests: formatMessage({
      defaultMessage: 'INTERESTS',
      id: 'driveToStore.footfallReport.buyerInsights.interests'
    }),
    men: formatMessage({ defaultMessage: 'Men', id: 'driveToStore.footfallReport.buyerInsights.gender.men' }),
    primaryValues: formatMessage({
      defaultMessage: 'PRIMARY VALUES',
      id: 'driveToStore.footfallReport.buyerInsights.primaryValues'
    }),
    statusLabel: formatMessage({
      defaultMessage: 'STATUS',
      id: 'driveToStore.footfallReport.buyerInsights.status.label'
    }),
    subHeader: formatMessage({
      defaultMessage: 'The average customer in this store',
      id: 'driveToStore.footfallReport.buyerInsights.subHeader'
    }),
    targetPersona: formatMessage({
      defaultMessage: 'TARGET PERSONA',
      id: 'driveToStore.footfallReport.buyerInsights.targetPersona.label'
    }),
    topBrands: formatMessage({
      defaultMessage: 'Top brands affinities',
      id: 'driveToStore.footfallReport.buyerInsights.topBrands'
    }),
    topCategories: formatMessage({
      defaultMessage: 'Top categories',
      id: 'driveToStore.footfallReport.buyerInsights.topCategories'
    }),
    women: formatMessage({ defaultMessage: 'Women', id: 'driveToStore.footfallReport.buyerInsights.gender.women' }),
    yearsOld: formatMessage({ defaultMessage: 'years old', id: 'driveToStore.footfallReport.buyerInsights.yrsOld' })
  };

  return <AudienceInsights data={data} labels={labels} />;
}
